import * as THREE from "three";

/**
 * A formation is a pure function: N -> { positions, colors }.
 * Every swarm input (procedural shape, SVG, text, image, real show file)
 * ultimately produces this same shape, so the morph engine never has to
 * care where the point cloud came from.
 */
export type Cloud = { positions: Float32Array; colors: Float32Array };
export type Formation = (n: number) => Cloud;

const TAU = Math.PI * 2;
const GOLDEN = Math.PI * (3 - Math.sqrt(5));

// cheap approx gaussian in ~[-1.5, 1.5]
function randn(): number {
  return Math.random() + Math.random() + Math.random() - 1.5;
}

const _c = new THREE.Color();
function hsl(out: Float32Array, i: number, h: number, s: number, l: number) {
  _c.setHSL(((h % 1) + 1) % 1, s, l);
  out[i * 3] = _c.r;
  out[i * 3 + 1] = _c.g;
  out[i * 3 + 2] = _c.b;
}

/** Fibonacci sphere — even coverage, hue wrapping around azimuth. */
const sphere: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const R = 2.5;
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0.5 : i / (n - 1);
    const inc = Math.acos(1 - 2 * t);
    const az = i * GOLDEN;
    p[i * 3] = Math.sin(inc) * Math.cos(az) * R;
    p[i * 3 + 1] = Math.cos(inc) * R;
    p[i * 3 + 2] = Math.sin(inc) * Math.sin(az) * R;
    hsl(c, i, 0.55 + Math.cos(inc) * 0.12, 0.85, 0.55);
  }
  return { positions: p, colors: c };
};

/** Solid geometric grid — a cbrt(N)-per-side 3D lattice (10k ≈ 22³). */
const cube: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const S = 2.3;
  const side = Math.max(2, Math.ceil(Math.cbrt(n)));
  const d = (side - 1) || 1;
  for (let i = 0; i < n; i++) {
    const ix = i % side;
    const iy = Math.floor(i / side) % side;
    const iz = Math.floor(i / (side * side));
    p[i * 3] = (ix / d - 0.5) * 2 * S;
    p[i * 3 + 1] = (iy / d - 0.5) * 2 * S;
    p[i * 3 + 2] = (iz / d - 0.5) * 2 * S;
    hsl(c, i, 0.52 + ((ix + iy + iz) / (3 * side)) * 0.32, 0.8, 0.55);
  }
  return { positions: p, colors: c };
};

/** Distribute N points evenly along a set of line segments [ax,ay,az,bx,by,bz]. */
function alongEdges(edges: number[][], n: number, hue: (x: number, y: number, z: number, t: number) => number): Cloud {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const lens = edges.map((e) => Math.hypot(e[3] - e[0], e[4] - e[1], e[5] - e[2]));
  const total = lens.reduce((a, b) => a + b, 0) || 1;
  const alloc = lens.map((l) => Math.max(1, Math.round((n * l) / total)));
  let gi = 0;
  for (let i = 1; i < alloc.length; i++) if (alloc[i] > alloc[gi]) gi = i;
  alloc[gi] = Math.max(1, alloc[gi] + (n - alloc.reduce((a, b) => a + b, 0)));
  let idx = 0;
  for (let e = 0; e < edges.length && idx < n; e++) {
    const [ax, ay, az, bx, by, bz] = edges[e];
    const m = alloc[e];
    for (let k = 0; k < m && idx < n; k++) {
      const t = m > 1 ? k / (m - 1) : 0.5;
      const x = ax + (bx - ax) * t, y = ay + (by - ay) * t, z = az + (bz - az) * t;
      p[idx * 3] = x; p[idx * 3 + 1] = y; p[idx * 3 + 2] = z;
      hsl(c, idx, hue(x, y, z, e / edges.length), 0.85, 0.58);
      idx++;
    }
  }
  for (; idx < n; idx++) {
    const s = (idx - 1) * 3;
    p[idx * 3] = p[s]; p[idx * 3 + 1] = p[s + 1]; p[idx * 3 + 2] = p[s + 2];
    c[idx * 3] = c[s]; c[idx * 3 + 1] = c[s + 1]; c[idx * 3 + 2] = c[s + 2];
  }
  return { positions: p, colors: c };
}

function tetraEdges(t: number[][]): number[][] {
  const pr = [[0, 1], [0, 2], [0, 3], [1, 2], [1, 3], [2, 3]];
  return pr.map(([a, b]) => [...t[a], ...t[b]]);
}

/** Merkaba — two interlocked tetrahedra (the star tetrahedron). */
const merkaba: Formation = (n) => {
  const s = 1.55;
  const t1 = [[1, 1, 1], [1, -1, -1], [-1, 1, -1], [-1, -1, 1]].map((v) => v.map((x) => x * s));
  const t2 = t1.map((v) => v.map((x) => -x));
  const edges = [...tetraEdges(t1), ...tetraEdges(t2)];
  return alongEdges(edges, n, (_x, _y, _z, t) => (t < 0.5 ? 0.5 : 0.9));
};

/** Icosahedron wireframe — 12 vertices, 30 edges. */
const icosahedron: Formation = (n) => {
  const g = (1 + Math.sqrt(5)) / 2, s = 1.25;
  const v = [
    [0, 1, g], [0, 1, -g], [0, -1, g], [0, -1, -g],
    [1, g, 0], [1, -g, 0], [-1, g, 0], [-1, -g, 0],
    [g, 0, 1], [g, 0, -1], [-g, 0, 1], [-g, 0, -1],
  ].map((p) => p.map((x) => x * s));
  const el2 = 4 * s * s;
  const edges: number[][] = [];
  for (let i = 0; i < v.length; i++)
    for (let j = i + 1; j < v.length; j++) {
      const d2 = (v[i][0] - v[j][0]) ** 2 + (v[i][1] - v[j][1]) ** 2 + (v[i][2] - v[j][2]) ** 2;
      if (Math.abs(d2 - el2) < 0.1 * el2) edges.push([...v[i], ...v[j]]);
    }
  return alongEdges(edges, n, (_x, y) => 0.55 + (y / 3) * 0.25);
};

/** Metatron's Cube — 13 nodes of the Fruit of Life, every pair connected. */
const metatron: Formation = (n) => {
  const d = 0.78;
  const pts: number[][] = [[0, 0, 0]];
  for (let k = 0; k < 6; k++) { const a = (k * Math.PI) / 3; pts.push([Math.cos(a) * d, Math.sin(a) * d, 0]); }
  for (let k = 0; k < 6; k++) { const a = (k * Math.PI) / 3; pts.push([Math.cos(a) * 2 * d, Math.sin(a) * 2 * d, 0]); }
  const scaled = pts.map((v) => [v[0] * 1.4, v[1] * 1.4, v[2]]);
  const edges: number[][] = [];
  for (let i = 0; i < scaled.length; i++)
    for (let j = i + 1; j < scaled.length; j++) edges.push([...scaled[i], ...scaled[j]]);
  return alongEdges(edges, n, (x, y) => 0.55 + (Math.atan2(y, x) / (Math.PI * 2) + 0.5) * 0.4);
};

/** (2,3) torus knot — a single continuous sacred curve. */
const torusKnot: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const R = 1.8, r = 0.6, P = 2, Q = 3;
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const cq = Math.cos(Q * t), sq = Math.sin(Q * t);
    p[i * 3] = (R + r * cq) * Math.cos(P * t);
    p[i * 3 + 1] = (R + r * cq) * Math.sin(P * t);
    p[i * 3 + 2] = r * sq;
    hsl(c, i, 0.5 + (i / n) * 0.5, 0.85, 0.58);
  }
  return { positions: p, colors: c };
};

/** Flower of Life — 19 overlapping circle outlines in the hex arrangement. */
const flowerOfLife: Formation = (n) => {
  const rc = 0.62;
  const centers: number[][] = [];
  const R2 = (2.05 * rc) ** 2;
  for (let q = -3; q <= 3; q++)
    for (let r = -3; r <= 3; r++) {
      const cx = rc * (q + r * 0.5), cy = rc * (r * Math.sqrt(3) / 2);
      if (cx * cx + cy * cy <= R2) centers.push([cx, cy]);
    }
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const base = Math.floor(n / centers.length);
  const rem = n % centers.length;
  let idx = 0;
  for (let ci = 0; ci < centers.length; ci++) {
    const m = base + (ci < rem ? 1 : 0);
    const [cx, cy] = centers[ci];
    for (let k = 0; k < m && idx < n; k++) {
      const a = (k / m) * Math.PI * 2;
      p[idx * 3] = (cx + Math.cos(a) * rc) * 1.6;
      p[idx * 3 + 1] = (cy + Math.sin(a) * rc) * 1.6;
      p[idx * 3 + 2] = 0;
      hsl(c, idx, 0.5 + (ci / centers.length) * 0.42, 0.85, 0.58);
      idx++;
    }
  }
  for (; idx < n; idx++) { const s = (idx - 1) * 3; p[idx * 3] = p[s]; p[idx * 3 + 1] = p[s + 1]; p[idx * 3 + 2] = p[s + 2]; c[idx * 3] = c[s]; c[idx * 3 + 1] = c[s + 1]; c[idx * 3 + 2] = c[s + 2]; }
  return { positions: p, colors: c };
};

/** Torus ring. */
const torus: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const R = 1.9, r = 0.75;
  for (let i = 0; i < n; i++) {
    const u = Math.random() * TAU;
    const v = Math.random() * TAU;
    p[i * 3] = (R + r * Math.cos(u)) * Math.cos(v);
    p[i * 3 + 1] = r * Math.sin(u);
    p[i * 3 + 2] = (R + r * Math.cos(u)) * Math.sin(v);
    hsl(c, i, 0.75 + v / TAU * 0.25, 0.85, 0.55);
  }
  return { positions: p, colors: c };
};

/** Double helix (DNA) — two counter-phased strands, colored by strand. */
const helix: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const turns = 4.5, rad = 1.5, H = 5.2;
  for (let i = 0; i < n; i++) {
    const t = i / n;
    const strand = i % 2;
    const ang = t * turns * TAU + strand * Math.PI;
    p[i * 3] = Math.cos(ang) * rad;
    p[i * 3 + 1] = (t - 0.5) * H;
    p[i * 3 + 2] = Math.sin(ang) * rad;
    hsl(c, i, strand ? 0.9 : 0.5, 0.85, 0.58);
  }
  return { positions: p, colors: c };
};

/** Barred spiral galaxy — warm core to cool rim, thin disk. */
const galaxy: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const arms = 4, spread = 0.55, R = 2.9;
  for (let i = 0; i < n; i++) {
    const rad = Math.sqrt(Math.random()) * R;
    const arm = i % arms;
    const ang = (arm / arms) * TAU + rad * 1.3 + randn() * spread * (rad / R + 0.15);
    p[i * 3] = Math.cos(ang) * rad + randn() * 0.08;
    p[i * 3 + 1] = randn() * 0.18 * (1 - rad / R) + randn() * 0.05;
    p[i * 3 + 2] = Math.sin(ang) * rad + randn() * 0.08;
    const f = rad / R; // 0 core -> 1 rim
    hsl(c, i, 0.09 + f * 0.55, 0.85, 0.62 - f * 0.12);
  }
  return { positions: p, colors: c };
};

/** Rippling grid plane. */
const wave: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const side = Math.ceil(Math.sqrt(n));
  const W = 6;
  for (let i = 0; i < n; i++) {
    const gx = i % side;
    const gz = ((i / side) | 0) % side;
    const x = (gx / (side - 1) - 0.5) * W;
    const z = (gz / (side - 1) - 0.5) * W;
    const y = Math.sin(x * 1.3) * Math.cos(z * 1.3) * 0.8;
    p[i * 3] = x;
    p[i * 3 + 1] = y;
    p[i * 3 + 2] = z;
    hsl(c, i, 0.55 + y * 0.15, 0.8, 0.5 + y * 0.12);
  }
  return { positions: p, colors: c };
};

/** Classic heart — parametric curve given a scattered depth shell. */
const heart: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  const s = 0.135;
  for (let i = 0; i < n; i++) {
    const t = Math.random() * TAU;
    const hx = 16 * Math.pow(Math.sin(t), 3);
    const hy =
      13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
    const shell = randn() * 0.35;
    p[i * 3] = hx * s + randn() * 0.05;
    p[i * 3 + 1] = hy * s + 0.2 + randn() * 0.05;
    p[i * 3 + 2] = shell;
    hsl(c, i, 0.98 + Math.random() * 0.04, 0.85, 0.55);
  }
  return { positions: p, colors: c };
};

/** Flat glowing ring. */
const ring: Formation = (n) => {
  const p = new Float32Array(n * 3);
  const c = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const ang = Math.random() * TAU;
    const rad = 1.7 + Math.random() * 0.6;
    p[i * 3] = Math.cos(ang) * rad;
    p[i * 3 + 1] = randn() * 0.08;
    p[i * 3 + 2] = Math.sin(ang) * rad;
    hsl(c, i, 0.5 + ang / TAU * 0.4, 0.85, 0.56);
  }
  return { positions: p, colors: c };
};

export const FORMATIONS = {
  sphere,
  cube,
  torus,
  helix,
  galaxy,
  wave,
  heart,
  ring,
  merkaba,
  icosahedron,
  metatron,
  torusKnot,
  flowerOfLife,
} satisfies Record<string, Formation>;

export type FormationKey = keyof typeof FORMATIONS;

export const FORMATION_LIST: { key: FormationKey; label: string }[] = [
  { key: "sphere", label: "Sphere" },
  { key: "cube", label: "Cube" },
  { key: "torus", label: "Torus" },
  { key: "helix", label: "Helix" },
  { key: "galaxy", label: "Galaxy" },
  { key: "wave", label: "Wave" },
  { key: "heart", label: "Heart" },
  { key: "ring", label: "Ring" },
  { key: "merkaba", label: "Merkaba" },
  { key: "icosahedron", label: "Icosahedron" },
  { key: "metatron", label: "Metatron" },
  { key: "torusKnot", label: "Torus Knot" },
  { key: "flowerOfLife", label: "Flower of Life" },
];

// The morph untangle now lives in the pure engine (engine/swarm.ts) with the
// rest of the physics core; re-exported here so every existing import site
// keeps working.
export { assign } from "./engine/swarm";
